import type {
  EquityChartData,
  EquityRangeResponse,
  EquityRangeValue,
  ETFChartData,
  IndexChartData,
  MutualFundChartData,
  PriceDataPoint,
} from '#/api/generated/fINRAMarketDataAPI.schemas';

import type {
  NormalizedSeriesPoint,
  NormalizedTimeseriesSeries,
  TimeseriesPoint,
  TimeseriesSeries,
} from './types';

const DAILY_SERIES_PLACEHOLDER_TIME = '00:00';

function hasMeaningfulRangeTime(time?: string | null) {
  return Boolean(time && time !== DAILY_SERIES_PLACEHOLDER_TIME);
}

function parseRangeTimestamp(value: Pick<EquityRangeValue, 'date' | 'time'>) {
  if (!value.date) {
    return undefined;
  }

  const [dayText, monthText, yearText] = value.date.split('-');
  const meaningfulTime = hasMeaningfulRangeTime(value.time)
    ? value.time
    : undefined;
  const [hourText = '12', minuteText = '0'] = meaningfulTime
    ? meaningfulTime.split(':')
    : [];

  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  const hour = Number(hourText);
  const minute = Number(minuteText);

  if (
    [day, month, year, hour, minute].some((part) => Number.isNaN(part)) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return undefined;
  }

  return Date.UTC(year, month - 1, day, hour, minute);
}

function parseManagedInvestmentTimestamp(dateText?: string | null) {
  if (!dateText) {
    return undefined;
  }

  const [yearText, monthText, dayText] = dateText.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);

  if (
    [year, month, day].some((part) => Number.isNaN(part)) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return undefined;
  }

  return Date.UTC(year, month - 1, day, 12, 0);
}

export function extractRangeValues(
  response?: EquityRangeResponse | null,
): EquityRangeValue[] {
  return response?.ts.results?.[0]?.data ?? [];
}

export function normalizeRangeValues(
  values: EquityRangeValue[],
): TimeseriesPoint[] {
  const points: TimeseriesPoint[] = [];

  for (const value of values) {
    const timestamp = parseRangeTimestamp(value);

    if (timestamp == null || value.close_price == null) {
      continue;
    }

    points.push({
      timestamp,
      closePrice: value.close_price,
      openPrice: value.open_price ?? undefined,
      highPrice: value.high_price ?? undefined,
      lowPrice: value.low_price ?? undefined,
      volume: value.volume ?? undefined,
      rawDate: value.date,
      rawTime: hasMeaningfulRangeTime(value.time) ? value.time : undefined,
    });
  }

  return points.sort((left, right) => left.timestamp - right.timestamp);
}

export function normalizeProductTypeChartData(
  chartData?: EquityChartData | ETFChartData | IndexChartData | null,
): TimeseriesPoint[] {
  return normalizeRangeValues(extractRangeValues(chartData?.data));
}

export function normalizeIndexChartData(
  chartData?: IndexChartData | null,
): TimeseriesPoint[] {
  return normalizeRangeValues(extractRangeValues(chartData?.data));
}

function extractManagedInvestmentPriceData(
  chartData?: MutualFundChartData | null,
): PriceDataPoint[] {
  return chartData?.data?.investments?.[0]?.timeSeries?.[0]?.data ?? [];
}

export function normalizeMutualFundChartData(
  chartData?: MutualFundChartData | null,
): TimeseriesPoint[] {
  const points: TimeseriesPoint[] = [];

  for (const value of extractManagedInvestmentPriceData(chartData)) {
    const timestamp = parseManagedInvestmentTimestamp(value.date);

    if (timestamp == null || value.value == null) {
      continue;
    }

    points.push({
      timestamp,
      closePrice: value.value,
      rawDate: value.date,
    });
  }

  return points.sort((left, right) => left.timestamp - right.timestamp);
}

export function downsampleTimeseries(
  points: TimeseriesPoint[],
  maxPoints: number,
): TimeseriesPoint[] {
  if (points.length <= maxPoints || maxPoints < 2) {
    return points;
  }

  if (maxPoints === 2) {
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];

    if (!firstPoint || !lastPoint) {
      return points;
    }

    return [firstPoint, lastPoint];
  }

  const firstPoint = points[0];

  if (!firstPoint) {
    return points;
  }

  const sampled: TimeseriesPoint[] = [firstPoint];
  const bucketSize = (points.length - 2) / (maxPoints - 2);

  for (let index = 0; index < maxPoints - 2; index += 1) {
    const nextIndex = 1 + Math.floor(index * bucketSize);
    const nextPoint = points[nextIndex];

    if (nextPoint) {
      sampled.push(nextPoint);
    }
  }

  const lastPoint = points[points.length - 1];

  if (lastPoint) {
    sampled.push(lastPoint);
  }

  return sampled.filter((point, index) => {
    if (index === 0) {
      return true;
    }

    const previousPoint = sampled[index - 1];
    return previousPoint ? point.timestamp !== previousPoint.timestamp : true;
  });
}

function buildTimestampPointMap(points: TimeseriesPoint[]) {
  return new Map(points.map((point) => [point.timestamp, point]));
}

export function getSharedComparisonTimestamps(
  seriesList: TimeseriesSeries[],
): number[] {
  if (!seriesList.length) {
    return [];
  }

  const sharedTimestamps = new Set(
    seriesList[0]?.points.map((point) => point.timestamp) ?? [],
  );

  for (const series of seriesList.slice(1)) {
    const seriesTimestamps = new Set(
      series.points.map((point) => point.timestamp),
    );

    for (const timestamp of sharedTimestamps) {
      if (!seriesTimestamps.has(timestamp)) {
        sharedTimestamps.delete(timestamp);
      }
    }
  }

  return Array.from(sharedTimestamps).sort((left, right) => left - right);
}

function normalizePointFromBaseline(
  point: TimeseriesPoint,
  baselineClosePrice: number,
  options?: { isContextOnly?: boolean },
): NormalizedSeriesPoint | null {
  if (baselineClosePrice === 0) {
    return null;
  }

  return {
    timestamp: point.timestamp,
    percentChange:
      ((point.closePrice - baselineClosePrice) / baselineClosePrice) * 100,
    ...(options?.isContextOnly ? { isContextOnly: true } : {}),
    sourcePoint: point,
  };
}

export function normalizeSeriesForComparison(
  seriesList: TimeseriesSeries[],
): NormalizedTimeseriesSeries[] {
  const sharedTimestamps = getSharedComparisonTimestamps(seriesList);

  if (!sharedTimestamps.length) {
    return [];
  }

  const baselineTimestamp = sharedTimestamps[0];

  if (baselineTimestamp == null) {
    return [];
  }

  return seriesList.flatMap((series) => {
    const pointMap = buildTimestampPointMap(series.points);
    const baselinePoint = pointMap.get(baselineTimestamp);

    if (!baselinePoint || baselinePoint.closePrice === 0) {
      return [];
    }

    const normalizedPoints = sharedTimestamps.flatMap((timestamp) => {
      const point = pointMap.get(timestamp);

      if (!point) {
        return [];
      }

      const normalizedPoint = normalizePointFromBaseline(
        point,
        baselinePoint.closePrice,
      );

      return normalizedPoint ? [normalizedPoint] : [];
    });

    const contextPoints =
      series.kind === 'comparison'
        ? series.points.flatMap((point) => {
            if (point.timestamp > baselineTimestamp) {
              return [];
            }

            const normalizedPoint = normalizePointFromBaseline(
              point,
              baselinePoint.closePrice,
              { isContextOnly: true },
            );

            return normalizedPoint ? [normalizedPoint] : [];
          })
        : [];

    const visiblePoints = [...contextPoints, ...normalizedPoints];

    const latestPoint = normalizedPoints[normalizedPoints.length - 1];

    if (!latestPoint) {
      return [];
    }

    const latestSourcePoint = latestPoint.sourcePoint;

    return [
      {
        ...series,
        points: visiblePoints,
        latestRawValue: latestSourcePoint.closePrice,
        latestPercentChange: latestPoint.percentChange,
      },
    ];
  });
}
